import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { GenerateReportButton } from '../components/GenerateReportButton';
import {
  AlertTriangle, Zap, Paperclip, Save, Loader2, Search, X, ShieldAlert,
  Library, ChevronRight, CheckCircle2, TriangleAlert, Pencil, Download, Archive,
} from 'lucide-react';

const PRIORITY_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};
const STATUS_COLORS = {
  'Not Assessed': 'bg-slate-100 text-slate-700 border-slate-200',
  'Non Compliant': 'bg-red-100 text-red-800 border-red-200',
  'Partially Compliant': 'bg-blue-100 text-blue-800 border-blue-200',
  'Compliant': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Not Applicable': 'bg-slate-50 text-slate-500 border-slate-200',
};
const STATUS_TRIGGER_COLORS = {
  'Not Assessed':           'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200',
  'Non Compliant':          'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
  'Partially Compliant':    'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  'Compliant':              'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
  'Not Applicable':         'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100',
};
const STATUS_OPTIONS = ['Not Assessed', 'Non Compliant', 'Partially Compliant', 'Compliant', 'Not Applicable'];

// Convert an ISO date string (YYYY-MM-DD) to display format DD-MM-YYYY.
// Returns empty string if input is empty/invalid.
function formatDateDMY(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}
const REASON_COLORS = {
  'Not assessed':          'bg-slate-100 text-slate-700 border-slate-200',
  'In progress':           'bg-amber-50 text-amber-800 border-amber-200',
  'Partially implemented': 'bg-blue-50 text-blue-800 border-blue-200',
  'Missing evidence':      'bg-purple-50 text-purple-800 border-purple-200',
};

function StatCard({ label, value, tone = 'slate', testid, icon: Icon }) {
  const toneMap = {
    slate: 'text-slate-900', red: 'text-red-600', orange: 'text-orange-600',
    amber: 'text-amber-600', emerald: 'text-emerald-600', blue: 'text-blue-600',
    muted: 'text-slate-400',
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="py-4 px-5 flex items-center gap-3">
        {Icon && <Icon className={`w-5 h-5 ${toneMap[tone]}`} />}
        <div>
          <div className={`text-2xl font-bold ${toneMap[tone]}`} data-testid={testid}>{value}</div>
          <div className="text-xs text-slate-500 mt-0.5">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function FrameworkChips({ affected }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(affected || []).map((f) => (
        <TooltipProvider key={f.framework.id} delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border cursor-help"
                style={{
                  borderColor: f.framework.color,
                  color: f.framework.color,
                  backgroundColor: `${f.framework.color}14`,
                }}
              >
                {f.framework.short_name}
                <span className="ml-1 px-1.5 rounded-full bg-white/70 text-[10px] font-semibold">
                  {f.requirements.length}
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs font-semibold">{f.framework.name}</p>
              <p className="text-xs mt-1">{(f.requirements || []).map((r) => r.code).join(', ')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
}

function GapDetailDialog({ open, onOpenChange, gap, canWrite, canEditPlan, canUploadEvidence, isAdmin, onSaved }) {
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [targetCloseDate, setTargetCloseDate] = useState('');
  const [gapOwner, setGapOwner] = useState('');
  const [gapDescription, setGapDescription] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [saving, setSaving] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  const loadEvidence = useCallback(async (controlId) => {
    try {
      const res = await axios.get(`${API}/master-controls/${controlId}/evidence`);
      setEvidenceFiles(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEvidenceFiles([]);
    }
  }, []);

  useEffect(() => {
    if (gap) {
      setCorrectiveAction(gap.corrective_action || '');
      setTargetCloseDate(gap.target_close_date || '');
      setGapOwner(gap.gap_owner || '');
      setGapDescription(gap.gap_description || '');
      setRecommendation(gap.recommendation || '');
      setSelectedFile(null);
      setUploadDescription('');
      loadEvidence(gap.control_id);
    }
  }, [gap, loadEvidence]);

  if (!gap) return null;

  const uploadEvidence = async () => {
    if (!selectedFile) {
      toast.error('Select a file to upload');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      if (uploadDescription) fd.append('description', uploadDescription);
      await axios.post(`${API}/master-controls/${gap.control_id}/evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Evidence uploaded: ${selectedFile.name}`);
      setSelectedFile(null);
      setUploadDescription('');
      const inp = document.getElementById(`gap-evidence-file-${gap.control_id}`);
      if (inp) inp.value = '';
      await loadEvidence(gap.control_id);
      onSaved?.(); // refresh parent counts
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const deleteEvidence = async (documentId) => {
    if (!window.confirm('Delete this evidence file?')) return;
    try {
      await axios.delete(`${API}/master-controls/${gap.control_id}/evidence/${documentId}`);
      toast.success('Evidence removed');
      await loadEvidence(gap.control_id);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete evidence');
    }
  };

  const downloadEvidence = async (documentId, fileName) => {
    try {
      const r = await axios.get(`${API}/documents/${documentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'evidence';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const downloadAllEvidence = async () => {
    try {
      const r = await axios.get(`${API}/master-controls/${gap.control_id}/evidence/download-all`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${gap.control_id}_evidence.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk download failed');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only persist overrides if user changed them away from the template
      const gdPayload = gapDescription === (gap.template_gap_description || '')
        ? null
        : (gapDescription || null);
      const recPayload = recommendation === (gap.template_recommendation || '')
        ? null
        : (recommendation || null);
      const planPayload = {
        corrective_action: correctiveAction || null,
        target_close_date: targetCloseDate || null,
        gap_owner: gapOwner || null,
      };
      const res = await axios.patch(
        `${API}/gap-engine/gaps/${gap.control_id}`,
        canWrite ? { ...planPayload, gap_description: gdPayload, recommendation: recPayload } : planPayload,
      );
      toast.success('Gap overlay saved');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save overlay');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gap-detail-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{gap.control_id}</span>
            <span>{gap.name}</span>
          </DialogTitle>
          <DialogDescription>{gap.domain}</DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-wrap gap-2">
          {gap.gap_reason && (
            <Badge className={`${REASON_COLORS[gap.gap_reason] || 'bg-slate-100 text-slate-700'} border`} data-testid="gap-reason-badge">
              <TriangleAlert className="w-3 h-3 mr-1" /> {gap.gap_reason}
            </Badge>
          )}
          {gap.priority && (
            <Badge className={`${PRIORITY_COLORS[gap.priority]} border`}>{gap.priority}</Badge>
          )}
          {gap.effort && <Badge variant="outline">Effort: {gap.effort}</Badge>}
          {gap.quick_win && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
              <Zap className="w-3 h-3 mr-1" /> Quick Win
            </Badge>
          )}
          <Badge className={`${STATUS_COLORS[gap.current_status] || ''} border`}>
            {gap.current_status}
          </Badge>
          <Badge variant="outline" className="text-xs">
            <Paperclip className="w-3 h-3 mr-1" /> {gap.evidence_count} evidence
          </Badge>
        </div>

        {/* Derived (read-only) sections */}
        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Description</h3>
          <p className="text-sm text-slate-800">{gap.description}</p>
        </section>

        {gap.gap_description && (
          <section className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">Gap description</h3>
            <p className="text-sm text-amber-900 whitespace-pre-line">{gap.gap_description}</p>
          </section>
        )}

        {/* Recommendation — editable per-tenant */}
        <section className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Recommendation</h3>
            {gap.recommendation_is_custom && (
              <span className="text-[10px] text-emerald-700 font-medium inline-flex items-center gap-0.5">
                <Pencil className="w-2.5 h-2.5" /> custom
              </span>
            )}
          </div>
          <Textarea
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            placeholder="Describe how this control should be implemented."
            rows={4}
            disabled={!canWrite}
            data-testid="gap-recommendation-input"
          />
          {gap.template_recommendation && gap.recommendation_is_custom && (
            <p className="text-[11px] text-slate-500 mt-1">
              <span className="font-medium">Template:</span> {gap.template_recommendation}
            </p>
          )}
        </section>

        {(gap.m365_guidance || gap.azure_guidance || gap.evidence_required) && (
          <section className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1 text-xs text-slate-700">
            {gap.evidence_required && <div><strong>Evidence required:</strong> {gap.evidence_required}</div>}
            {gap.m365_guidance && <div><strong>M365:</strong> {gap.m365_guidance}</div>}
            {gap.azure_guidance && <div><strong>Azure:</strong> {gap.azure_guidance}</div>}
          </section>
        )}

        {/* Affected frameworks */}
        <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Affected frameworks &amp; requirements
          </h3>
          {(gap.affected_frameworks || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">No framework mappings.</p>
          ) : (
            <div className="space-y-2">
              {(gap.affected_frameworks || []).map((f) => (
                <div key={f.framework.id} className="flex items-start gap-3 text-sm">
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border shrink-0"
                    style={{ borderColor: f.framework.color, color: f.framework.color, backgroundColor: `${f.framework.color}14` }}
                  >
                    {f.framework.short_name}
                  </span>
                  <div className="text-xs text-slate-700 flex-1">
                    {(f.requirements || []).map((r) => (
                      <span
                        key={r.requirement_id}
                        data-testid={`gap-req-${r.code}`}
                        className="inline-block font-mono mr-2"
                        title={r.title || 'Official title not set'}
                      >
                        {r.code}{r.title ? ` — ${r.title}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Assessor comments passthrough */}
        {(gap.assessor_comments || gap.notes || gap.implementation_details) && (
          <section className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1">
            {gap.implementation_details && <div><strong>Implementation details:</strong> {gap.implementation_details}</div>}
            {gap.notes && <div><strong>Assessment notes:</strong> {gap.notes}</div>}
            {gap.assessor_comments && <div><strong>Assessor comments:</strong> {gap.assessor_comments}</div>}
          </section>
        )}

        {/* Gap description — editable per-tenant */}
        <section className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
              Actual gap description
            </h3>
            {gap.gap_description_is_custom && (
              <span className="text-[10px] text-indigo-600 font-medium inline-flex items-center gap-0.5">
                <Pencil className="w-2.5 h-2.5" /> custom
              </span>
            )}
          </div>
          <Textarea
            value={gapDescription}
            onChange={(e) => setGapDescription(e.target.value)}
            placeholder="Describe what is actually missing or incomplete for this control in your environment."
            rows={3}
            disabled={!canWrite}
            data-testid="gap-description-input"
          />
          {gap.template_gap_description && gap.gap_description_is_custom && (
            <p className="text-[11px] text-slate-500 mt-1">
              <span className="font-medium">Template:</span> {gap.template_gap_description}
            </p>
          )}
        </section>

        {/* Editable overlay */}
        <section className="mt-4 rounded-lg border border-slate-300 bg-white p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            Corrective action plan
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Gap owner</label>
              <Input
                value={gapOwner}
                onChange={(e) => setGapOwner(e.target.value)}
                placeholder="Assign an owner"
                disabled={!canEditPlan}
                data-testid="gap-owner-input"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Closure date</label>
              <Input
                type="date"
                value={targetCloseDate}
                onChange={(e) => setTargetCloseDate(e.target.value)}
                disabled={!canEditPlan}
                data-testid="gap-target-date-input"
                className="mt-1"
              />
              {targetCloseDate && (
                <p className="text-[11px] text-slate-500 mt-1">Displayed as {formatDateDMY(targetCloseDate)}</p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-500">Corrective action</label>
            <Textarea
              value={correctiveAction}
              onChange={(e) => setCorrectiveAction(e.target.value)}
              placeholder="Steps this tenant will take to close the gap"
              rows={3}
              disabled={!canEditPlan}
              data-testid="gap-corrective-action-input"
              className="mt-1"
            />
          </div>
        </section>

        {/* Evidence upload */}
        <section className="mt-4 rounded-lg border border-blue-200 bg-blue-50/30 p-3" data-testid="gap-evidence-section">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-800 inline-flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> Evidence
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-blue-800/70">{evidenceFiles.length} file{evidenceFiles.length === 1 ? '' : 's'}</span>
              {isAdmin && evidenceFiles.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadAllEvidence}
                  className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="gap-evidence-download-all-btn"
                  title="Download all evidence as a ZIP"
                >
                  <Archive className="w-3.5 h-3.5 mr-1" /> ZIP
                </Button>
              )}
            </div>
          </div>
          {evidenceFiles.length > 0 && (
            <ul className="space-y-1.5 mb-3" data-testid="gap-evidence-list">
              {evidenceFiles.map((ev) => (
                <li key={ev.document_id} className="flex items-center gap-2 rounded border border-blue-100 bg-white px-2 py-1.5 text-xs">
                  <Paperclip className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-800 truncate flex-1" title={ev.file_name}>{ev.file_name}</span>
                  <span className="text-slate-500 shrink-0">{Math.max(1, Math.round((ev.file_size || 0) / 1024))} KB</span>
                  <button
                    type="button"
                    onClick={() => downloadEvidence(ev.document_id, ev.file_name)}
                    className="text-blue-600 hover:text-blue-800 p-0.5"
                    data-testid={`gap-evidence-download-${ev.document_id}`}
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {canUploadEvidence && (
                    <button
                      type="button"
                      onClick={() => deleteEvidence(ev.document_id)}
                      className="text-red-500 hover:text-red-700 p-0.5"
                      data-testid={`gap-evidence-delete-${ev.document_id}`}
                      title="Delete"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canUploadEvidence && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  id={`gap-evidence-file-${gap.control_id}`}
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                  className="text-xs flex-1"
                  data-testid="gap-evidence-file-input"
                />
                <Button
                  size="sm"
                  onClick={uploadEvidence}
                  disabled={uploading || !selectedFile}
                  data-testid="gap-evidence-upload-btn"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Paperclip className="w-3.5 h-3.5 mr-1" />}
                  Upload
                </Button>
              </div>
              <Input
                placeholder="Description (optional)"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                disabled={uploading}
                className="text-xs"
                data-testid="gap-evidence-description-input"
              />
              <p className="text-[11px] text-slate-500">Max 25 MB per file.</p>
            </div>
          )}
        </section>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gap-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!canEditPlan || saving} data-testid="gap-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GapEnginePage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin';
  const [summary, setSummary] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quickWinFilter, setQuickWinFilter] = useState('all');
  const [detailGap, setDetailGap] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(null);

  const updateStatus = async (controlId, newStatus) => {
    setSavingStatus(controlId);
    try {
      await axios.put(`${API}/master-controls/${controlId}/assessment`, { status: newStatus });
      toast.success(`${controlId} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingStatus(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (frameworkFilter !== 'all') params.set('framework', frameworkFilter);
      if (priorityFilter !== 'all') params.set('priority', priorityFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (quickWinFilter !== 'all') params.set('quick_win', quickWinFilter === 'yes' ? 'true' : 'false');
      if (search.trim()) params.set('search', search.trim());
      const [gRes, sRes] = await Promise.all([
        axios.get(`${API}/gap-engine/gaps?${params.toString()}`),
        axios.get(`${API}/gap-engine/summary`),
      ]);
      setGaps(gRes.data.gaps || []);
      setSummary(sRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load gaps');
    } finally {
      setLoading(false);
    }
  }, [search, frameworkFilter, priorityFilter, statusFilter, quickWinFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load(); }, [viewingOrg]);

  const openDetail = (gap) => { setDetailGap(gap); setDialogOpen(true); };

  const clearFilters = () => {
    setSearch(''); setFrameworkFilter('all'); setPriorityFilter('all');
    setStatusFilter('all'); setQuickWinFilter('all');
  };

  const activeFilterCount = useMemo(() => [
    search.trim(), frameworkFilter !== 'all', priorityFilter !== 'all',
    statusFilter !== 'all', quickWinFilter !== 'all',
  ].filter(Boolean).length, [search, frameworkFilter, priorityFilter, statusFilter, quickWinFilter]);

  return (
    <div className="space-y-6" data-testid="gap-engine-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" />
            <span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span>
            <span>Gap Engine</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-600" />
            Gap Assessment
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            One gap per deficient master control, across every mapped framework. No duplicates.
            Overlay fields (owner, target date, corrective action) are editable and tenant-scoped.
          </p>
        </div>
        <GenerateReportButton
          options={[{ key: 'gap_report', label: 'Gap Report (PDF)', report_type: 'gap_report' }]}
          variant="default"
          className="bg-orange-600 hover:bg-orange-700 text-white"
          testid="gap-generate-report-btn"
        />
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard label="Total gaps" value={summary.total_gaps} tone="slate" icon={AlertTriangle} testid="gap-total-count" />
            <StatCard label="Quick wins" value={summary.quick_wins} tone="emerald" icon={Zap} testid="gap-quick-wins" />
            <StatCard label="Critical" value={summary.by_priority?.Critical || 0} tone="red" testid="gap-critical" />
            <StatCard label="High" value={summary.by_priority?.High || 0} tone="orange" testid="gap-high" />
            <StatCard label="Medium" value={summary.by_priority?.Medium || 0} tone="amber" testid="gap-medium" />
            <StatCard label="Low" value={summary.by_priority?.Low || 0} tone="slate" testid="gap-low" />
          </div>

          {/* Status breakdown across all in-scope controls */}
          <div>
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2 tracking-wide">
              Control status breakdown
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gap-status-breakdown">
              <StatCard label="Non Compliant" value={summary.by_status?.['Non Compliant'] || 0} tone="red" testid="gap-status-non-compliant" />
              <StatCard label="Partially Compliant" value={summary.by_status?.['Partially Compliant'] || 0} tone="blue" testid="gap-status-partial" />
              <StatCard label="Not Applicable" value={summary.by_status?.['Not Applicable'] || 0} tone="muted" testid="gap-status-not-applicable" />
            </div>
          </div>
        </>
      )}

      {/* Per-framework rollup pills — click to filter */}
      {summary?.by_framework?.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase mr-1">Filter by framework:</span>
            <button
              onClick={() => setFrameworkFilter('all')}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition ${
                frameworkFilter === 'all'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid="gap-fw-pill-all"
            >
              All
              <span className={`ml-1 px-1.5 rounded-full text-[10px] font-semibold ${
                frameworkFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
              }`}>{summary?.total_gaps || 0}</span>
            </button>
            {summary.by_framework.map((f) => {
              const active = frameworkFilter === f.framework.id;
              return (
                <button
                  key={f.framework.id}
                  onClick={() => setFrameworkFilter(f.framework.id)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition ${
                    active ? 'text-white' : 'hover:opacity-80'
                  }`}
                  style={active
                    ? { borderColor: f.framework.color, backgroundColor: f.framework.color }
                    : { borderColor: f.framework.color, color: f.framework.color, backgroundColor: `${f.framework.color}14` }}
                  data-testid={`gap-fw-pill-${f.framework.id}`}
                >
                  {f.framework.short_name}
                  <span className={`ml-1 px-1.5 rounded-full text-[10px] font-semibold ${
                    active ? 'bg-white/25 text-white' : 'bg-white/70'
                  }`}>{f.count}</span>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="gap-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, name, description, owner…"
              className="pl-9"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 flex-shrink-0">
            <Select value={frameworkFilter} onValueChange={setFrameworkFilter}>
              <SelectTrigger data-testid="gap-filter-framework"><SelectValue placeholder="Framework" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All frameworks</SelectItem>
                {(summary?.by_framework || []).map((f) => (
                  <SelectItem key={f.framework.id} value={f.framework.id}>{f.framework.short_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger data-testid="gap-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {['Critical', 'High', 'Medium', 'Low'].map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="gap-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {['Not Assessed', 'Non Compliant', 'Partially Compliant', 'Compliant', 'Non Compliant'].map((s) => (
                  <SelectItem key={s} value={s} data-testid={`gap-filter-status-option-${s.replace(/\s+/g, '-')}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={quickWinFilter} onValueChange={setQuickWinFilter}>
              <SelectTrigger data-testid="gap-filter-quickwin"><SelectValue placeholder="Quick win" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="yes">Quick wins only</SelectItem>
                <SelectItem value="no">Non quick-wins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          Showing <span className="font-semibold text-slate-800" data-testid="gap-result-count">{gaps.length}</span> of {summary?.total_gaps || 0} active gaps
          {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`}
        </span>
        {activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="gap-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">#</TableHead>
                  <TableHead className="min-w-[240px]">Description</TableHead>
                  <TableHead className="min-w-[180px]">Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="text-center">QW</TableHead>
                  <TableHead className="text-center">Evidence</TableHead>
                  <TableHead className="min-w-[200px]">Affected frameworks</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="min-w-[130px]">Closure date</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading gaps…
                    </TableCell>
                  </TableRow>
                ) : gaps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-16 text-slate-500">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500 inline mb-2" />
                      <div>No open gaps match the current filters.</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  gaps.map((g, idx) => (
                    <TableRow
                      key={g.control_id}
                      className={`cursor-pointer hover:bg-slate-50 ${g.dismissed ? 'opacity-60' : ''}`}
                      onClick={() => openDetail(g)}
                      data-testid={`gap-row-${g.control_id}`}
                    >
                      <TableCell
                        className="font-mono text-slate-600 text-sm"
                        data-testid={`gap-seq-${g.control_id}`}
                      >
                        {idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900 line-clamp-2" data-testid={`gap-description-${g.control_id}`}>
                          {g.gap_description || <span className="italic text-slate-400">No gap description yet — click row to add</span>}
                        </div>
                        <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                          {g.name}
                          {g.gap_description_is_custom && (
                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-indigo-600 font-medium">
                              <Pencil className="w-2.5 h-2.5" /> custom
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const cur = g.current_status || 'Not Assessed';
                          const trigCls = STATUS_TRIGGER_COLORS[cur] || STATUS_TRIGGER_COLORS['Not Assessed'];
                          return (
                            <Select
                              value={cur}
                              onValueChange={(v) => updateStatus(g.control_id, v)}
                              disabled={!canWrite || savingStatus === g.control_id}
                            >
                              <SelectTrigger
                                data-testid={`gap-status-select-${g.control_id}`}
                                className={`h-7 text-xs font-medium border ${trigCls} px-2`}
                              >
                                {savingStatus === g.control_id ? (
                                  <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                                ) : (
                                  <SelectValue />
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                  <SelectItem key={s} value={s} data-testid={`gap-status-option-${g.control_id}-${s.replace(/\s+/g, '-')}`}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {g.priority && (
                          <Badge className={`${PRIORITY_COLORS[g.priority]} border text-xs`}>{g.priority}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {g.quick_win && <Zap className="w-4 h-4 text-emerald-600 inline" />}
                      </TableCell>
                      <TableCell className="text-center">
                        {g.evidence_count > 0 ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />{g.evidence_count}
                          </Badge>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <FrameworkChips affected={g.affected_frameworks} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {g.gap_owner || <span className="text-slate-400 italic">Unassigned</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700" data-testid={`gap-target-date-${g.control_id}`}>
                        {g.target_close_date ? (
                          <span className="font-mono">{formatDateDMY(g.target_close_date)}</span>
                        ) : (
                          <span className="text-slate-400 italic text-xs">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <GapDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        gap={detailGap}
        canWrite={canWrite}
        canEditPlan={canWrite || user?.role === 'client'}
        canUploadEvidence={
          user?.role === 'admin' ||
          user?.role === 'super_admin' ||
          user?.role === 'client' ||
          user?.role === 'auditor'
        }
        isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
        onSaved={(fresh) => {
          setDetailGap(fresh);
          load();
        }}
      />
    </div>
  );
}
